// M004-F0751 (WebUI #193, design docs/design/M004-F0751-Credit-Notes-Tab.md). The Credit notes tab on the Ledger landing page: every credit
// note of one organisation (org-wide credit_note.list, no user_id), filtered Active (default) / Fully used / Expired / All, with the unused
// total of the Active set. The caller has gated the tab on billing_transactions_view or ledger_manage; Core and the Ledger enforce the same.
// Layout and table pattern follow the Invoices tab (M004-F0749); the CSS classes are the Invoices tab's.
import React, { useEffect, useState } from 'react';
import { creditNoteApi } from '../../services/creditNoteApi';
import { errorInfo, formatDay, money } from '../invoices/invoiceModel';
import { downloadCreditNotePdf } from './creditNotePdf';
import ApplyToAccountDrawer from './ApplyToAccountDrawer';
import { canApplyToAccountRow } from './applyToAccountModel';
import '../invoices/InvoicesTab.css';
import {
  CN_FILTERS,
  CN_FILTER_LABELS,
  expiryText,
  isListIncomplete,
  orgListRequest,
  rowsForFilter,
  statusLabel,
  unusedTotalMinor,
} from './creditNoteModel';

// WebUI #213: the table shows 50 rows per page. The totals and the cap notice still read the full sets, never the page.
const CN_PAGE_SIZE = 50;

const listOf = (response) => {
  const data = response?.data?.data;
  if (!data || !Array.isArray(data.credit_notes)) throw new Error('The Ledger answered with a credit-note list this page cannot read.');
  return data.credit_notes;
};

export default function CreditNotesTab({ organisations = [], orgId, onSelectOrg }) {
  const [filter, setFilter] = useState('active');
  const [page, setPage] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const [pdfError, setPdfError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [applying, setApplying] = useState(null);
  const [notice, setNotice] = useState(null);
  const [state, setState] = useState({ status: 'loading', active: [], view: [], error: null });
  const org = organisations.find((item) => String(item.id) === String(orgId));
  const orgName = org?.name || `organisation ${orgId}`;
  // M004-F0757: the Apply to account column and drawer are admin-only (ledger_manage, set by the landing page per organisation).
  const manage = Boolean(org?.manage);

  // The Active set always feeds the unused total, whatever filter is shown; the filtered set is read only when it differs.
  useEffect(() => {
    if (!orgId) return undefined;
    let current = true;
    setState({ status: 'loading', active: [], view: [], error: null });
    (async () => {
      try {
        const active = listOf(await creditNoteApi.list(orgId, orgListRequest('active')));
        const view = filter === 'active' ? active : listOf(await creditNoteApi.list(orgId, orgListRequest(filter)));
        if (current) setState({ status: 'ready', active, view, error: null });
      } catch (cause) {
        if (!current) return;
        const error = errorInfo(cause);
        setState({ status: error.unsupported ? 'unsupported' : 'error', active: [], view: [], error });
      }
    })();
    return () => { current = false; };
  }, [orgId, filter, attempt]);

  // A new organisation starts on page 1 (the filter buttons do the same in their handler).
  useEffect(() => { setPage(0); }, [orgId]);

  const ready = state.status === 'ready';
  const shown = ready ? rowsForFilter(state.view, filter) : [];
  // The page is clamped so a shrinking list (after a refetch) never leaves the table past its last page.
  const pageCount = Math.max(1, Math.ceil(shown.length / CN_PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const pageStart = current * CN_PAGE_SIZE;
  const pageRows = shown.slice(pageStart, pageStart + CN_PAGE_SIZE);
  const pageEnd = pageStart + pageRows.length;
  const incomplete = ready && (isListIncomplete(state.active) || isListIncomplete(state.view));
  const totalHidden = ready && isListIncomplete(state.active);
  const label = CN_FILTER_LABELS[filter];

  const download = async (row) => {
    setPdfError('');
    setBusyId(row.credit_note_id);
    try {
      await downloadCreditNotePdf(orgId, row);
    } catch (cause) {
      const info = errorInfo(cause);
      const text = info.code && !info.message.includes(info.code) ? `${info.code}: ${info.message}` : info.message;
      setPdfError(`${row.credit_note_number}: ${text}`);
    } finally {
      setBusyId('');
    }
  };

  return (
    <section className="inv-tab" aria-labelledby="cn-tab-title" data-testid="credit-notes-tab">
      <header className="inv-header">
        <div>
          <span className="inv-eyebrow">Ledger</span>
          <h2 id="cn-tab-title">Credit notes</h2>
          <p className="inv-copy">Every credit note issued for the organisation. Unused credit is what the organisation still holds for its customers.</p>
        </div>
        <label className="inv-org-select">Organisation
          <select value={String(orgId)} onChange={(event) => onSelectOrg?.(event.target.value)}>
            {organisations.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
          </select>
        </label>
      </header>

      {state.status === 'loading' ? (
        <div className="inv-skeleton" data-testid="credit-notes-skeleton" role="status" aria-busy="true" aria-label="Loading credit notes">
          {[0, 1, 2, 3].map((n) => <div className="inv-skeleton-row" key={n} aria-hidden="true" />)}
          <span className="inv-sr">Loading credit notes…</span>
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="inv-error" role="alert" data-testid="credit-notes-error">
          <strong>The credit notes could not be loaded.</strong>
          <span>{state.error.code ? `${state.error.code}: ` : ''}{state.error.message}</span>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>Retry</button>
        </div>
      ) : null}

      {state.status === 'unsupported' ? (
        <p className="inv-state" role="status" data-testid="credit-notes-unsupported">Credit notes are not available from this Ledger yet.</p>
      ) : null}

      {pdfError ? <p className="inv-error" role="alert" data-testid="credit-note-pdf-error">{pdfError}</p> : null}
      {notice ? <p className="inv-state" role="status" data-testid="cn-account-notice"><strong>{notice.message}</strong> <span>{notice.figures}</span></p> : null}

      {ready && totalHidden ? (
        <p className="inv-state" role="status" data-testid="credit-notes-incomplete">
          This list is incomplete: the Ledger returned its 2000-row maximum, so the unused total is not shown.
        </p>
      ) : null}

      {ready && !totalHidden ? (
        <p className="inv-count" data-testid="credit-notes-unused-total">
          Unused credit the organisation holds: <strong>{money(unusedTotalMinor(state.active), 'GBP')}</strong>
        </p>
      ) : null}

      {ready && incomplete && !totalHidden ? (
        <p className="inv-state" role="status" data-testid="credit-notes-incomplete-view">This list is incomplete: the Ledger returned its 2000-row maximum for this filter.</p>
      ) : null}
      {(ready || state.status === 'loading') ? (
          <div className="inv-filters" role="group" aria-label="Filter credit notes">
            {CN_FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                data-testid={`credit-notes-filter-${key}`}
                onClick={() => { setFilter(key); setPage(0); }}
              >
                {CN_FILTER_LABELS[key]}
              </button>
            ))}
          </div>
      ) : null}

      {ready && state.view.length === 0 && filter === 'all' ? (
        <p className="inv-state" data-testid="credit-notes-empty">No credit notes have been issued for {orgName} yet.</p>
      ) : null}

      {ready && !(state.view.length === 0 && filter === 'all') ? (
        <>
          <p className="inv-count" role="status">{shown.length} {shown.length === 1 ? 'credit note' : 'credit notes'} ({label})</p>
          {shown.length === 0 ? (
            <p className="inv-state" data-testid="credit-notes-no-match">No {label.toLowerCase()} credit notes for {orgName}.</p>
          ) : (
            <table className="inv-table" data-testid="credit-notes-table">
              <caption className="inv-sr">Credit notes, {label}</caption>
              <thead>
                <tr>
                  <th scope="col">Number</th><th scope="col">Customer</th><th scope="col">Issued</th>
                  <th scope="col" className="inv-num">Original</th><th scope="col" className="inv-num">Used</th>
                  <th scope="col" className="inv-num">Unused</th><th scope="col">Expiry</th><th scope="col">Status</th><th scope="col">Document</th>
                  {manage ? <th scope="col">Account</th> : null}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.credit_note_id} data-testid="credit-note-row">
                    <th scope="row" data-label="Number">{row.credit_note_number}</th>
                    <td data-label="Customer">{row.customer_name}</td>
                    <td data-label="Issued">{formatDay(row.issue_date)}</td>
                    <td data-label="Original" className="inv-num">{money(row.amount_minor, row.currency)}</td>
                    <td data-label="Used" className="inv-num">{money(Math.max(0, Number(row.amount_minor || 0) - Number(row.available_minor || 0)), row.currency)}</td>
                    <td data-label="Unused" className="inv-num" data-testid="credit-note-unused">{money(row.available_minor, row.currency)}</td>
                    <td data-label="Expiry" data-testid="credit-note-expiry">{expiryText(row)}</td>
                    <td data-label="Status"><span className="inv-pill">{statusLabel(row)}</span></td>
                    <td data-label="Document">
                      <button type="button" disabled={busyId === row.credit_note_id} aria-label={`Download PDF for ${row.credit_note_number}`} onClick={() => download(row)}>
                        {busyId === row.credit_note_id ? 'Preparing…' : 'Download PDF'}
                      </button>
                    </td>
                    {manage ? (
                      <td data-label="Account">
                        {canApplyToAccountRow(row) ? (
                          <button type="button" data-testid="credit-note-apply-account" aria-label={`Apply ${row.credit_note_number} to account`} onClick={() => setApplying(row)}>Apply to account</button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {shown.length > 0 ? (
            <nav className="inv-filters" aria-label="Credit notes pages" data-testid="credit-notes-pager">
              <button type="button" data-testid="credit-notes-page-prev" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous</button>
              <span data-testid="credit-notes-range">Showing {pageStart + 1}–{pageEnd} of {shown.length}</span>
              <span data-testid="credit-notes-page-label">Page {current + 1} of {pageCount}</span>
              <button type="button" data-testid="credit-notes-page-next" disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)}>Next</button>
            </nav>
          ) : null}
        </>
      ) : null}
      {manage && applying ? (
        <ApplyToAccountDrawer
          note={applying}
          orgId={orgId}
          onClose={() => setApplying(null)}
          onApplied={(done) => { setApplying(null); setNotice(done); setAttempt((n) => n + 1); }}
          onUnconfirmed={() => setAttempt((n) => n + 1)}
        />
      ) : null}
    </section>
  );
}
